import { type Tracker, useTracker } from '@archipro-website/tracker';
import { useSharedTiles } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';

export const useCategoryTilesTracking = (
    gridRef: React.RefObject<HTMLElement>
) => {
    const tracker = useTracker();
    const trackingConfig = {
        onClick: (
            targetRef: React.RefObject<HTMLElement>,
            itemID?: number,
            professionalID?: number,
            section?: string
        ) => {
            switch (section) {
                case 'CategoryPageRecentlyViewed':
                    roccoLogRecentlyViewedItemClick(
                        tracker,
                        itemID,
                        professionalID
                    );
                    break;

                case 'ProjectUsedProducts':
                    roccoLogProjectUsedProductsItemClick(
                        tracker,
                        itemID,
                        professionalID
                    );
                    break;
                
                case 'ProfessionalProducts':
                    roccoLogProfessionalProductsItemClick(
                        tracker,
                        itemID,
                        professionalID
                    );
                    break;

                case 'ProjectUsedProfessionals':
                    roccoLogProjectUsedProfessionalsItemClick(
                        tracker,
                        itemID,
                        professionalID
                    );
                    break;

                case 'ProjectRelated':
                    roccoLogProjectRelatedItemClick(
                        tracker,
                        itemID,
                        professionalID
                    );
                    break;
                
                case 'ProfessionalProjects':
                    roccoLogProfessionalProjectsItemClick(
                        tracker,
                        itemID,
                        professionalID
                    );
                    break;

                default:
                    roccoLogDirectoryItemClick(
                        tracker,
                        gridRef,
                        targetRef,
                        itemID,
                        professionalID
                    );
            }
        },

        onTileMediaSliderSlideChange: (
            slideIndex: number,
            mediaSrc: string,
            itemID?: number,
            professionalID?: number
        ) => {
            tracker.log('directoryTileMediaSlideChange', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: itemID,
                    ProfessionalID: professionalID,
                    ExtraData: JSON.stringify({
                        slideIndex,
                        mediaSrc,
                    }),
                },
            });
        },
    };

    useSharedTiles({
        trackingConfig,
    });
};

/**
 * Logs a directory item click event with position information.
 *
 * This function calculates the item's position in a grid layout and logs it as a tracking event.
 * The position is calculated based on:
 * - Row number (starting from 1)
 * - Column number (starting from 1)
 * - Number of columns per row
 * - Absolute position in the list (starting from 1)
 *
 * The position information is formatted as: "Row,Column,CountPerRow,AbsolutePosition"
 * For example: "2,3,4,7" means:
 * - Item is in row 2
 * - Item is in column 3
 * - There are 4 columns per row
 * - Item is the 7th item overall
 *
 * @see {@link logDirectoryItemClick} The original implementation this is based on
 */
const roccoLogDirectoryItemClick = (
    tracker: Tracker,
    gridRef: React.RefObject<HTMLElement>,
    targetRef: React.RefObject<HTMLElement>,
    itemID?: number,
    professionalID?: number
) => {
    const gridData = getGridPosition(gridRef, targetRef);

    if (gridData) {
        const { row, column, columnsPerRow, position } = gridData;

        tracker.log('directoryTileClick', {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: itemID,
                ProfessionalID: professionalID,
                // format: Row,Column,CountPerRow,AbsolutePosition
                ExtraData: `${row},${column},${columnsPerRow},${position}`,
            },
        });
    }
};

const roccoLogRecentlyViewedItemClick = (
    tracker: Tracker,
    itemID?: number,
    professionalID?: number
) => {
    tracker.log('recentlyViewedTileClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
        },
    });
};

const roccoLogProjectUsedProductsItemClick = (
    tracker: Tracker,
    itemID?: number,
    professionalID?: number
) => {
    tracker.log('ProductUsedProjectClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
        },
    });
};

const roccoLogProfessionalProductsItemClick = (
    tracker: Tracker,
    itemID?: number,
    professionalID?: number
) => {
    tracker.log('ProfessionalProductClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
        },
    });
};

const roccoLogProjectUsedProfessionalsItemClick = (
    tracker: Tracker,
    itemID?: number,
    professionalID?: number
) => {
      tracker.log('ProfessionalUsedProjectClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
        },
    });
};

const roccoLogProfessionalProjectsItemClick = (
    tracker: Tracker,
    itemID?: number,
    professionalID?: number
) => {
    tracker.log('ProfessionalProjectClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
        },
    });
};

const roccoLogProjectRelatedItemClick = (
    tracker: Tracker,
    itemID?: number,
    professionalID?: number
) => {
    tracker.log('MoreProjectsFromClick', {
        url: new URL(window.location.href),
        data: {
            LibraryItemID: itemID,
            ProfessionalID: professionalID,
        },
    });
};

/**
 * Calculates the position of an element within a CSS Grid layout.
 *
 * This function determines:
 * - The row and column position of the target element
 * - The total number of columns in the grid
 * - The absolute position of the element in the grid sequence
 *
 * The calculation takes into account:
 * - Grid column and row gaps
 * - The relative position of the target element to the grid container
 * - The dimensions of both the grid and target element
 *
 * @param {React.RefObject<HTMLElement>} gridRef - Reference to the grid container element
 * @param {React.RefObject<HTMLElement>} targetRef - Reference to the target element within the grid
 * @returns {Object | undefined} Position data containing:
 *   - column: The column number (1-based)
 *   - row: The row number (1-based)
 *   - columnsPerRow: Total number of columns in the grid
 *   - position: Absolute position in the grid sequence (1-based)
 *   Returns undefined if either reference is invalid
 */
const getGridPosition = (
    gridRef: React.RefObject<HTMLElement>,
    targetRef: React.RefObject<HTMLElement>
) => {
    const gridElement = gridRef.current;
    const element = targetRef.current;

    if (!element || !gridElement) return;

    const gridStyles = window.getComputedStyle(gridElement);

    // Get grid properties
    const gridColumns = gridStyles.gridTemplateColumns.split(' ').length;
    const gridColumnGap = parseInt(gridStyles.columnGap) || 0;
    const gridRowGap = parseInt(gridStyles.rowGap) || 0;

    // Get element bounds
    const elementBounds = element.getBoundingClientRect();
    const gridBounds = gridElement.getBoundingClientRect();

    // Calculate cell size (including gap)
    const cellWidth = (gridBounds.width + gridColumnGap) / gridColumns;
    const cellHeight =
        (gridBounds.height + gridRowGap) / (gridBounds.height / cellWidth);

    // Calculate position
    const relativeLeft = elementBounds.left - gridBounds.left;
    const relativeTop = elementBounds.top - gridBounds.top;

    const column = Math.floor(relativeLeft / cellWidth) + 1;
    const row = Math.floor(relativeTop / cellHeight) + 1;

    return {
        column,
        row,
        columnsPerRow: gridColumns,
        position: gridColumns * (row - 1) + column,
    };
};
